
import { SOURCE_SYSTEM } from "@/constans";
import {
  IClientData,
  IClientDetailData,
  IClientParams,
  ICountry,
  ILimit,
  IThirdcode,
  IUpdateClient,
  IUser,
} from "@/entities";
import { i18n } from "@/i18n";
import {
  IGenericResponse,
  IPageResponse,
  IValidateSolClientParams,
  IValidateSolClientResponse,
} from "@/services";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import Multiselect from "vue-multiselect";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";
import LimitsTable from "@/components/limits/LimitsTable.vue";

@Component({
  name: "client-add-page",
  components: {
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
    LimitsTable,
  },
})
export default class ClientAddPage extends Vue {
  @Action("fetchClientsPage") fetchClientsPage!: (
    params: IClientParams
  ) => Promise<IPageResponse<IClientData> | undefined>;
  @Action("saveUpdateClient") saveUpdateClient!: (
    updateClient: IUpdateClient
  ) => Promise<IGenericResponse<IClientDetailData> | undefined>;
  @Action("validateSolClient") validateSolClient!: (payload: {
    countryCode: string;
    params: IValidateSolClientParams;
  }) => Promise<IValidateSolClientResponse | undefined>;

  @Getter("getLoggedUser") loggedUser?: IUser;
  @Getter("getCountriesPage") countriesPage?: IPageResponse<ICountry>;

  public validateInSolInput = true;
  public countrySelected: ICountry | null = null;
  public sourceSystemSelected: string | null = null;
  public clientCodeInput: string | null = null;
  private clientCode?: string;
  private solClientExists = false;

  private clientOid?: string;
  public clientDescriptionInput: string | null = null;
  public clientSmartCashIdInput: string | null = null;
  public visibleCountry = true;
  public thirdcodeList?: IThirdcode[] = [];
  public limitList: ILimit[] = [];

  constructor() {
    super();
  }

  get isCodeDisabled(): boolean {
    if (this.validateInSolInput) {
      return !this.countrySelected || !this.sourceSystemSelected;
    } else {
      return !this.countrySelected;
    }
  }

  get showAdditionalFields(): boolean {
    const result = this.clientCodeInput !== null && this.clientCodeInput !== "";
    if (this.validateInSolInput) {
      return result && this.solClientExists;
    }
    return result;
  }

  get optionsCountries(): ICountry[] {
    if (this.loggedUser?.admin) {
      this.visibleCountry = true;
      return this.countriesPage?.content || [];
    } else if (this.loggedUser?.country?.id) {
      this.visibleCountry = false;
      this.countrySelected = this.loggedUser.country;

      return (
        this.countriesPage?.content.filter(
          (country) => this.loggedUser?.country?.id === country.id
        ) || []
      );
    }
    return [];
  }

  get optionsSourceSystem(): any {
    return [
      i18n.t("label.none"),
      SOURCE_SYSTEM.SIGII,
      SOURCE_SYSTEM.GENESIS,
      SOURCE_SYSTEM.CORPOINT,
      SOURCE_SYSTEM.PROFAT,
    ];
  }

  async mounted(): Promise<void> {
    extend("client_exists", {
      validate: async () => {
        let countryValid;
        if (this.loggedUser?.admin) {
          countryValid = (
            await (this.$refs.country as InstanceType<typeof ValidationProvider>).validate()
          ).valid;
        }
        const sourceSystemValid = (
          await (this.$refs.sourceSystem as InstanceType<typeof ValidationProvider>).validate()
        ).valid;

        if (this.loggedUser?.admin) {
          return countryValid && sourceSystemValid ? this.validateCode() : true;
        } else {
          return sourceSystemValid ? this.validateCode() : true;
        }
      },
      message: () => this.$t("validation.messages.clientNotFound") as string,
    });
  }

  async validateCode(): Promise<boolean> {
    const res = await this.validateSolClient({
      countryCode: this.countrySelected!.code!,
      params: {
        sourceSystem: this.sourceSystemSelected!,
        clientCode: this.clientCodeInput!,
      },
    });

    this.solClientExists = res ? res.exito : false;

    if (res?.exito) {
      this.clientDescriptionInput = res?.entidad.desNombre;
      this.clientOid = res?.entidad.idEntidad;
      this.clientCode = res?.entidad.codEntidad;
      this.thirdcodeList = res.entidad.codigosTercero.map((e) => {
        return {
          system: e.sistemaTerceroCodigo,
          code: e.valor,
        } as IThirdcode;
      });
      return true;
    } else {
      this.clientDescriptionInput = null;
      this.clientOid = undefined;
      this.clientCode = undefined;
      this.thirdcodeList = [];
      return false;
    }
  }

  validateInSolInputChange(): void {
    this.sourceSystemSelected = null;
    this.clientCodeInput = null;
    this.clientCode = undefined;
    this.solClientExists = false;
    this.clientOid = undefined;
    this.clientDescriptionInput = null;
    this.clientSmartCashIdInput = null;
    this.thirdcodeList = [];
    this.limitList = [];
  }

  countrySelectedChange(): void {
    // Reset same fields
    this.validateInSolInputChange();
  }

  clientCodeInputChange(): void {
    this.clientCode = undefined;
    this.solClientExists = false;
    this.clientOid = undefined;
    this.clientDescriptionInput = null;
    this.clientSmartCashIdInput = null;
    this.thirdcodeList = [];
    this.limitList = [];
  }

  async onSubmit(): Promise<void> {
    const res = await this.saveUpdateClient({
      oidSol: this.clientOid || "",
      codeSol: this.validateInSolInput ? this.clientCode! : this.clientCodeInput!,
      uniqueCode: "",
      description: this.clientDescriptionInput!,
      idSmartcash: this.clientSmartCashIdInput!,
      active: true,
      country: this.countrySelected!.id,
      thirdcodes: this.thirdcodeList,
      limits: this.limitList,
    });

    if (res?.status === 1) {
      // Refresh clients table
      await this.fetchClientsPage({
        size: 10,
        page: 0,
      });

      this.$router.back();
    }
  }
}
